import { Alert, Box, Heading, Text, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';

import { useAppSettings } from '#providers/app-settings-provider';

import {
  buildGoogleMapsEmbedUrl,
  describeMap,
  getMapValidationMessage,
  type MapConfig,
} from './map-config';

import './map-block.scss';

export type MapBlockVariant = 'default' | 'grid-cell';

export interface MapBlockProps {
  config: MapConfig;
  parseError?: string | null;
  variant?: MapBlockVariant;
}

export function MapBlock({
  config,
  parseError,
  variant = 'default',
}: MapBlockProps) {
  const { googleMapsEmbedApiKey } = useAppSettings();
  const validationMessage = getMapValidationMessage(config);
  const isGridCell = variant === 'grid-cell';
  const previewUrl = useMemo(
    () =>
      googleMapsEmbedApiKey
        ? buildGoogleMapsEmbedUrl(config, googleMapsEmbedApiKey)
        : null,
    [config, googleMapsEmbedApiKey],
  );

  const iframeStyle = isGridCell
    ? {
        width: '100%',
        minHeight: 0,
        height: '100%',
        border: 0,
        borderRadius: '12px',
      }
    : {
        width: '100%',
        minHeight: `${config.height}px`,
        border: 0,
        borderRadius: '12px',
      };

  return (
    <VStack
      align="stretch"
      gap={isGridCell ? 2 : 3}
      className={isGridCell ? 'map-block map-block--grid-cell' : 'map-block'}
      flex={isGridCell ? 1 : undefined}
      minH={isGridCell ? 0 : undefined}
      h={isGridCell ? '100%' : undefined}
    >
      <Box flexShrink={0}>
        <Heading as="h2" size={isGridCell ? 'xs' : 'sm'}>
          Map
        </Heading>
        <Text fontSize={isGridCell ? 'xs' : 'sm'} color="fg.muted">
          {describeMap(config)}
        </Text>
      </Box>

      {previewUrl ? (
        <Box
          className="markdown-map-node__frame"
          flex={isGridCell ? 1 : undefined}
          minH={isGridCell ? 0 : undefined}
          display={isGridCell ? 'flex' : undefined}
          flexDirection={isGridCell ? 'column' : undefined}
        >
          <iframe
            title={describeMap(config)}
            src={previewUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            style={iframeStyle}
          />
        </Box>
      ) : (
        <Box
          className="markdown-map-node__placeholder"
          flex={isGridCell ? 1 : undefined}
          minH={isGridCell ? 0 : undefined}
        >
          <Text fontSize="sm" color="fg.muted">
            {googleMapsEmbedApiKey
              ? 'Complete the map fields to preview the embed.'
              : 'Google Maps key is not configured for this environment.'}
          </Text>
        </Box>
      )}

      {parseError ? (
        <Alert.Root status="warning" borderRadius="md" flexShrink={0}>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid map config</Alert.Title>
            <Alert.Description>{parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {!parseError && validationMessage ? (
        <Alert.Root status="warning" borderRadius="md" flexShrink={0}>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Incomplete map config</Alert.Title>
            <Alert.Description>{validationMessage}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
    </VStack>
  );
}
